import type { IHeaderParams } from "ag-grid-community";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu, MenuItem, MenuSection, TriggerButton, TriggerButtonSize, useMenu } from "widgets";

import { getUniqueAttributeId } from "../../../app-utils/sample.js";
import type { Attribute, FileType, ProjectFile, ProjectFileSearch } from "../../../graphql.js";
import { getGroupedNearPathsBeforeLastUnderscore } from "../../../modules/files/utils/file.js";
import type { IEditable, IEditCellChange } from "../../../redux/modules/data-editing.js";
import { searchProjectFiles } from "../../../redux/modules/files.js";
import { setSampleGridChanges } from "../../../redux/modules/sample-editing.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import {
  ATTRIBUTE_FILE_TYPES,
  ATTRIBUTE_ID,
  ATTRIBUTE_TYPE,
  CELL_TYPE,
  EDIT_GRID_COLUMN_ACTIONS,
} from "../../../settings/settings.js";
import type { HeaderCellProps } from "../../core/grid/cells/headers/HeaderCell.js";
import type { ICustomColDef } from "../../core/grid/DataTable.js";
import { ModalControlledOverlay } from "../../core/overlays/index.js";
import SampleEditGridCopyDialog from "./SampleEditGridCopyDialog.js";

export const SampleEditingMenuTrigger = (props: HeaderCellProps & IHeaderParams) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { triggerRef, menuRef, triggerProps, menuProps, state } = useMenu<HTMLButtonElement, HTMLElement>({
    offset: 1,
    placement: "bottom start",
  });

  const [copyAction, setCopyAction] = useState<EDIT_GRID_COLUMN_ACTIONS>(null);
  const dataGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);
  const setEditingGridChanges = useCallback((change) => dispatch(setSampleGridChanges(change)), []);

  return (
    <>
      <TriggerButton
        icon={"more_vert"}
        label={"Menu"}
        size={TriggerButtonSize.Small}
        onPress={() => state.toggle()}
        {...triggerProps}
        ref={triggerRef}
      />
      {state.isOpen && (
        <SampleEditingMenu
          {...props}
          menuRef={menuRef}
          menuProps={menuProps}
          onClose={state.close}
          setCopyAction={setCopyAction}
        />
      )}
      {copyAction && (
        <ModalControlledOverlay close={() => setCopyAction(null)} isOpen={true}>
          {({ close }) => (
            <SampleEditGridCopyDialog
              column={props.column.getUserProvidedColDef()}
              dataGrid={dataGrid}
              setSampleGridChanges={setEditingGridChanges}
              copyAction={copyAction}
              onClose={close}
            />
          )}
        </ModalControlledOverlay>
      )}
    </>
  );
};

interface SampleEditingMenu {
  menuRef: any;
  menuProps: any;
  onClose: any;
  setCopyAction: (action: EDIT_GRID_COLUMN_ACTIONS) => void;
}

const SampleEditingMenu = (props: HeaderCellProps & IHeaderParams & SampleEditingMenu) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const dataRows = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);

  const isAutoIncrementEnabled = useMemo(() => {
    const colDef: ICustomColDef = props.column.getUserProvidedColDef();
    const attribute: Attribute = colDef.attribute;
    return (
      attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT ||
      attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL
    );
  }, []);

  const isAutoCopyEnabled = useMemo(() => {
    const colDef: ICustomColDef = props.column.getUserProvidedColDef();
    return colDef.type !== CELL_TYPE.FILE && colDef.type !== CELL_TYPE.ORGANISM;
  }, []);

  const isAutoFillWithFilesEnabled = useMemo(() => {
    const colDef: ICustomColDef = props.column.getUserProvidedColDef();
    return colDef.type === CELL_TYPE.FILE;
  }, []);

  const clearAll = useCallback(() => {
    const colDef: ICustomColDef = props.column.getUserProvidedColDef();
    const colId = colDef.colId;

    const cellChanges: IEditCellChange[] = dataRows
      .filter((row) => !row.locked && colDef.attribute?.editable)
      .map((row) => {
        if (!row.editing[colId].readOnly) {
          const editCellChange: IEditCellChange = {
            rowId: row.id,
            column: { columnId: colDef.colId },
            newValue: null,
          };
          return editCellChange;
        }
      })
      .filter((change: any) => !!change); // filter out null values (we filter AFTER the map because the index is important

    return dispatch(setSampleGridChanges(cellChanges));
  }, []);

  const samplesWithNameAndEmptyFileCells = useMemo(() => {
    const colDef: ICustomColDef = props.column.getUserProvidedColDef();
    const fileAttribute: Attribute = colDef.attribute;
    const fileAttributeId: string = getUniqueAttributeId(fileAttribute);
    // get all valid sample names
    const sampleNameAttributeId: number = ATTRIBUTE_ID.SAMPLE_NAME;

    const sampleNames: string[] = dataRows
      .filter((row) => !row.locked)
      // search only where files have not been set yet
      .filter((row) => {
        const existingFileSelection = row[fileAttributeId] as ProjectFile[];
        return !existingFileSelection?.length;
      })
      .map((row) => row[sampleNameAttributeId] as string)
      .filter((sampleName) => sampleName?.length);
    return sampleNames;
  }, []);

  const autoFillWithFiles = useCallback(() => {
    const colDef: ICustomColDef = props.column.getUserProvidedColDef();
    const fileAttribute: Attribute = colDef.attribute;
    const fileAttributeId: string = getUniqueAttributeId(fileAttribute);
    const fileFilterExpression: string | undefined = getFilePickerSelectableFilter(fileAttribute.fileType);
    const sampleNameAttributeId: number = ATTRIBUTE_ID.SAMPLE_NAME;

    props.api.setGridOption("loading", true);

    // trigger the search and make suggestions from the results
    dispatch(searchProjectFiles(samplesWithNameAndEmptyFileCells, fileFilterExpression)).then(
      (fileSearches: ProjectFileSearch[]) => {
        const sampleUpdates: IEditCellChange[] = [];
        dataRows
          .filter((row) => !row.locked)
          .forEach((row) => {
            const sampleName: string = row[sampleNameAttributeId] as string;
            const matchingFiles: ProjectFile[] = fileSearches
              .find((fileSearch: ProjectFileSearch) => fileSearch.searchTerm === sampleName)
              ?.projectFiles?.sort(
                // shorter names should be better since we use a startWith to match samples names to file names
                (projectFileA, projectFileB) => (projectFileA.fileName.length < projectFileB.fileName.length ? -1 : 1),
              );

            if (matchingFiles) {
              // take into account the paired-end scenario, where 2 files might match
              const groupedFiles = getGroupedNearPathsBeforeLastUnderscore(matchingFiles);
              const bestFileMatches: ProjectFile[] = groupedFiles[0];
              const sampleUpdateData: IEditCellChange = {
                rowId: row.id,
                column: { columnId: fileAttributeId },
                newValue: bestFileMatches,
              };
              sampleUpdates.push(sampleUpdateData);
            }
          });

        return dispatch(setSampleGridChanges(sampleUpdates)).then(() => {
          props.api.setGridOption("loading", false);
        });
      },
    );
  }, [samplesWithNameAndEmptyFileCells]);

  return (
    <Menu
      onClose={props.onClose}
      aria-label={"Analyze menu"}
      domProps={props.menuProps}
      ref={props.menuRef}
      disabledKeys={!samplesWithNameAndEmptyFileCells?.length ? ["Auto Fill"] : undefined}
    >
      <MenuSection title={nanoid()} hideTitle key={nanoid()}>
        <MenuItem
          id={"Clear All"}
          key={"clear_all"}
          onAction={clearAll}
          icon={"cancel"}
          description={"Clear all values of this column"}
        >
          Clear All
        </MenuItem>

        {isAutoIncrementEnabled && (
          <MenuItem
            id={"Auto Increment"}
            key={"auto_incr"}
            onAction={() => props.setCopyAction(EDIT_GRID_COLUMN_ACTIONS.AUTO_INCREMENT)}
            //iconImage={{ path: item.imgPath || item.img, width: 77, height: 44 }}
            icon={"123"}
            description={"Provide some incremental pattern for this column"}
          >
            Auto Increment
          </MenuItem>
        )}
        {isAutoCopyEnabled && (
          <MenuItem
            id={"Auto Copy"}
            key={"auto_copy"}
            onAction={() => props.setCopyAction(EDIT_GRID_COLUMN_ACTIONS.AUTO_COPY)}
            icon={"copy_all"}
            description={"Provide a value to apply to all rows"}
          >
            Auto Copy
          </MenuItem>
        )}
        {isAutoFillWithFilesEnabled && (
          <MenuItem
            id={"Auto Fill"}
            key={"auto_fill"}
            onAction={autoFillWithFiles}
            icon={"location_searching"}
            description={
              "Automatically find files that best match the sample name for every individual row. Applies only to empty cells."
            }
          >
            Map to Matching Project Files
          </MenuItem>
        )}
      </MenuSection>
    </Menu>
  );
};

function getFilePickerSelectableFilter(fileType: FileType): string {
  const attributeFileType = fileType?.id;

  if (attributeFileType === ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA) {
    return "\\.(fastq|fq)(\\.(gz|gzip|zip|bz2|lzo|xz))?$";
  } else if (attributeFileType === ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA) {
    return "\\.(bam|sam)(\\.(gz|gzip|zip|bz2|lzo|xz))?$";
  }
}
